'use client';

import { useMemo } from 'react';
import { usePricing } from '@/contexts/PricingContext';
import Schema, { buildBrandFromProduct } from './Schema';
import { DATA_POINTS, PEOPLE_AUDIENCE } from './constants';
import { buildProductGroup, buildVariantProduct } from './ProductGroupSchema';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

// Main component for Product JSON-LD schema
const ProductSchema = ({
  slug,
  metadata,
  images = [],
  productData,
  productMasthead,
  productRating,
  productReviews,
  productDescription,
}) => {
  const pricing = usePricing();
  const siteSettings = useSiteSettings();

  // Plain URLs: an ImageObject here would carry the rights fields that make
  // Google report Image Metadata, which SEO doesn't want on the PDP.
  const imageUrls = useMemo(
    () => [...new Set([metadata?.og_image?.url, ...images.map((image) => image.url)].filter(Boolean))],
    [metadata, images]
  );

  // Extract the price calculation function from the pricing context.
  // We use this specific function as a dependency (instead of the whole 'pricing' object) 
  // to avoid recalculating all prices if unrelated pricing context changes.
  const getVariantPricing = pricing.variant;

  // Loop through ALL variants and build a dictionary mapping each variant ID to its sale/full price.
  const variantPrices = useMemo(() => {
    const map = {};
    productData.shopifyProduct?.variants?.forEach((v) => {
      const calculatedPrices = getVariantPricing(v);
      map[v.id] = {
        sale: calculatedPrices.sale,
        full: calculatedPrices.full,
      };
    });
    return map;
  }, [productData.shopifyProduct, getVariantPricing]);

  // Build the main schema node (ProductGroup for multi-variant or Product for single-variant)
  const schema = useMemo(() => {
    const product = productData.shopifyProduct;
    if (!product) return [];

    const isProductGroup = product.variants?.length > 1;

    let mainSchema;

    if (isProductGroup) {
      mainSchema = buildProductGroup({
        product,
        slug,
        metadata,
        variantPrices,
        imageUrls,
        siteSettings,
        productRating,
        productReviews,
        productDescription,
      });
    } else {
      // Single variant fallback -> Standard Product Schema
      const variant = product.variants?.[0];
      const variantProduct = buildVariantProduct(variant, product, siteSettings, variantPrices[variant?.id], slug, {
        isSingleProduct: true,
        productRating,
        productReviews,
      });

      if (variantProduct) {
        mainSchema = {
          ...variantProduct,
          // Overrides/additions for standard product
          name: product.title || metadata?.title || '',
          description: productDescription || product.seo?.description || metadata?.description || undefined,
          brand: buildBrandFromProduct(),
          category: product.productType || undefined,
          audience: PEOPLE_AUDIENCE,
          mainEntityOfPage: {
            '@id': `${DATA_POINTS.url.product}/${slug}#webpage`,
          },
          ...(imageUrls.length > 0 && { image: imageUrls }),
        };
      }
    }

    return [
      mainSchema,
    ];
  }, [
    productData.shopifyProduct,
    slug,
    metadata,
    variantPrices,
    imageUrls,
    siteSettings,
    productRating,
    productReviews,
    productDescription,
  ]);

  return <Schema schema={schema} />;
};

export default ProductSchema;
